/**
 * `LabelChip` — the dubplate miniaturised. `docs/design-direction.md` §8.2, §9.1.
 *
 * The same procedural label as `PlateLabel`, reduced to what survives at 44px and
 * 24px: grooves plus two ink rings in the track's own hashed pair. It is readable at
 * that size precisely because it is *pattern*, not image — which is the whole reason
 * the crate needs no thumbnail column and the app needs no cover-art wall (§12.22).
 * A cover, when one exists, appears inside the full-size label and nowhere else.
 *
 * It spins with playback and freezes on pause, exactly like the record it stands for
 * (§7.2): the now-playing row's chip keeps turning in the ledger, and the mini
 * player's 24px chip mirrors the transport. `animation-play-state` is written inline
 * for the same two reasons it is on `Disc` — it cannot be lost to a cascade order,
 * and it is the form a test with no CSS engine can read — and the angle is never
 * reset, so a chip that stops mid-groove stays there.
 *
 * It is `aria-hidden`: the row, the mini player and the sleeve all name the track in
 * text beside it, and a screen reader announcing a decorative record twice per row is
 * noise.
 */

import type { CSSProperties, JSX } from 'react';

import { derivePlate, plateCssVars, plateFromSeed } from '@bl/shared';
import type { Plate } from '@bl/shared';

import styles from './components.module.css';
import { cx } from './primitives/Rule';

/** 44px in the ledger (§8.2), 24px in the mini player (§9.1). There is no third size. */
export type LabelChipSize = 24 | 44;

export interface LabelChipProps {
  readonly title: string;
  readonly performer?: string | null | undefined;
  readonly messageId: number;
  /** `tracks.plate_seed` when the row carries it; otherwise the plate is re-derived. */
  readonly seed?: number | undefined;
  readonly size?: LabelChipSize;
  /** Mirrors playback. A pause freezes the chip mid-groove; it never resets. */
  readonly spinning?: boolean;
  readonly className?: string | undefined;
}

export function LabelChip({
  title,
  performer = null,
  messageId,
  seed,
  size = 44,
  spinning = false,
  className,
}: LabelChipProps): JSX.Element {
  const plate: Plate =
    seed === undefined ? derivePlate(performer, title, messageId) : plateFromSeed(seed, messageId);

  const style: CSSProperties = {
    ...(plateCssVars(plate) as CSSProperties),
    animationPlayState: spinning ? 'running' : 'paused',
  };

  return (
    <span
      className={cx(styles.chip, className)}
      style={style}
      data-size={String(size)}
      data-pair={String(plate.pair)}
      data-spinning={spinning ? 'true' : 'false'}
      aria-hidden="true"
    />
  );
}
